<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import type { Image, PortableTextBlock } from '@sanity/types';
import BaseImage from '@pkg/ui/components/base/BaseImage.vue';
import BaseText from '@pkg/ui/components/base/BaseText/BaseText.vue';
import { TitleBlock as BaseTitle } from '../Title';
import FlipCounter from './FlipCounter.vue';

const props = defineProps<{
  title?: PortableTextBlock[],
  preTitle?: PortableTextBlock[],
  text?: PortableTextBlock[],
  backgroundImage?: Image,
  initialNumber: number,
  intervalSeconds: number,
  startTime: string,
}>();

const calculateCount = () => {
  const start = new Date(props.startTime);
  const now = new Date();
  const secondsElapsed = Math.floor((now.getTime() - start.getTime()) / 1000);
  const intervalsElapsed = Math.floor(secondsElapsed / props.intervalSeconds);
  return props.initialNumber + Math.max(0, intervalsElapsed);
};

const totalNumber = ref(calculateCount());
const numbersArray = computed(() => [...String(totalNumber.value)].map(Number));

const updateCounter = () => {
  totalNumber.value = calculateCount();
};

let intervalId: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  const intervalMs = props.intervalSeconds * 1000;
  intervalId = setInterval(updateCounter, intervalMs);
});

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId);
  }
});
</script>

<template>
  <div class="simple-counter">
    <BaseImage
      v-if="backgroundImage"
      :image="backgroundImage"
      alt="counter image"
      title="counter image"
      class="counter__image"
    />
    <BaseTitle
      v-if="preTitle || title"
      :pre-title="preTitle"
      :title="title"
      variant="center"
      size="large"
    />
    <div class="counter-wrapper">
      <FlipCounter :numbers-array="numbersArray" :show-commas="true" />
    </div>
    <div v-if="text" class="text-wrapper">
      <BaseText variant="center">
        {{ text }}
      </BaseText>
    </div>
  </div>
</template>

<style scoped>
.simple-counter {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.counter-wrapper {
  padding-top: 16px;
  width: 100%;
}

.text-wrapper {
  @screen tablet-wide {
    max-width: 600px;
  }
}

.counter__image {
  width: 180px;
  height: auto;
  align-self: center;

  @screen tablet-wide {
    width: 280px;
  }
}
</style>
